import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  History,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  X,
  Calendar,
  Activity,
  ArrowRight,
  User as UserIcon,
  ExternalLink,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { cn, formatDateTime, toISODate } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useAuditGlobal,
  useAuditAcciones,
  useAuditActores,
  type AuditFilters,
  type AuditLogEntry,
} from "./api";

const ACTION_LABEL: Record<string, string> = {
  "contrato.update": "Contrato editado",
  "contrato.create": "Contrato creado",
  "contrato.suspend": "Contrato suspendido",
  "contrato.reactivate": "Contrato reactivado",
  // rc144: transiciones hechas por el worker MikroTik + re-anclaje de ciclo
  "contrato.router_error": "Router no aplicó el cambio",
  "contrato.router_ok": "Router aplicó el cambio",
  "contrato.reancla_ciclo": "Ciclo re-anclado",
  "contrato.ciclo_revision_descartada": "Revisión de ciclo descartada",
  "contrato.cancel": "Contrato cancelado",
  "contrato.regenerate_password": "Password PPPoE regenerado",
  "cliente.update": "Cliente editado",
  "cliente.create": "Cliente creado",
  "factura.create": "Factura emitida",
  "factura.anular": "Factura anulada",
  "pago.create": "Pago aplicado",
  "pago.anular": "Pago anulado",
};

// Variantes de badge por familia de acción
const ACTION_VARIANT: Record<
  string,
  "success" | "warning" | "destructive" | "secondary" | "default"
> = {
  "contrato.create": "success",
  "cliente.create": "success",
  "factura.create": "success",
  "pago.create": "success",
  "contrato.update": "default",
  "cliente.update": "default",
  "contrato.suspend": "destructive",
  "contrato.cancel": "destructive",
  "factura.anular": "destructive",
  "pago.anular": "destructive",
  "contrato.reactivate": "warning",
  "contrato.router_error": "destructive",
  "contrato.router_ok": "success",
  "contrato.reancla_ciclo": "default",
  "contrato.ciclo_revision_descartada": "secondary",
  "contrato.regenerate_password": "secondary",
};

const ENTIDAD_LABEL: Record<string, string> = {
  cliente: "Cliente",
  contrato: "Contrato",
  factura: "Factura",
  pago: "Pago",
};

const FIELD_LABEL: Record<string, string> = {
  pppoe_user: "Usuario PPPoE",
  fecha_corte: "Día de corte",
  precio_override: "Precio override",
  descuento_pct: "% descuento",
  dias_gracia_override: "Días gracia",
  exento_facturacion: "Exento facturación",
  exento_monitoreo: "Exento monitoreo",
  estatus: "Estatus",
  notas: "Notas",
  notas_internas: "Notas internas",
  fecha_activacion: "Fecha activación",
  fecha_suspension: "Fecha suspensión",
  fecha_cancelacion: "Fecha cancelación",
  razon_social: "Razón social",
  nombre_comercial: "Nombre comercial",
  rfc: "RFC",
  tipo: "Tipo",
  password_rotated: "Password rotado",
  "wireless.cpe_mac": "MAC CPE",
  "wireless.cpe_vendor": "Vendor CPE",
  "wireless.cpe_modelo": "Modelo CPE",
  "wireless.cpe_ip_management": "IP mgmt CPE",
  "wireless.ssid_conexion": "SSID",
  "wireless.frecuencia_mhz": "Frecuencia",
  "wireless.polarizacion": "Polarización",
  "fiber.onu_mac": "MAC ONU",
  "fiber.onu_vendor": "Vendor ONU",
  "fiber.onu_serial": "Serial ONU",
  "fiber.olt_puerto": "Puerto OLT",
  "fiber.splitter_nivel": "Splitter",
};

function labelFor(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sí" : "no";
  if (typeof v === "string" && v === "") return "(vacío)";
  return String(v);
}

function extractChanges(
  antes: Record<string, unknown> | null,
  despues: Record<string, unknown> | null,
): { field: string; before: unknown; after: unknown }[] {
  const keys = new Set<string>([
    ...Object.keys(antes ?? {}),
    ...Object.keys(despues ?? {}),
  ]);
  keys.delete("__motivo__");
  keys.delete("__extra__");
  return Array.from(keys)
    .map((k) => ({ field: k, before: antes?.[k], after: despues?.[k] }))
    .filter((c) => c.before !== c.after);
}

function shiftDay(iso: string | null, deltaDays: number): string {
  const d = iso ? new Date(iso + "T12:00:00") : new Date();
  d.setDate(d.getDate() + deltaDays);
  return toISODate(d);
}

export function AuditGlobalPage() {
  const todayISO = toISODate(new Date());
  // null = sin filtro de fecha (ver todo)
  const [fechaDia, setFechaDia] = useState<string | null>(todayISO);
  const [accion, setAccion] = useState<string>("");
  const [entidad, setEntidad] = useState<string>("");
  const [actorUserId, setActorUserId] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const q = useDebounce(searchInput, 300);

  // Al escribir en el buscador, si hay texto, ignoramos filtro de fecha
  // automáticamente para no ocultar resultados del pasado.
  const effectiveFechaDia = q.trim() ? null : fechaDia;

  const filters: AuditFilters = useMemo(
    () => ({
      page, size: 50,
      accion: accion || undefined,
      entidad: entidad || undefined,
      actor_user_id: actorUserId || undefined,
      fecha_dia: effectiveFechaDia ?? undefined,
      q: q.trim() || undefined,
    }),
    [page, accion, entidad, actorUserId, effectiveFechaDia, q],
  );

  const { data, isLoading, isFetching } = useAuditGlobal(filters);
  const { data: acciones = [] } = useAuditAcciones();
  const { data: actores = [] } = useAuditActores();

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  const hasAnyFilter =
    !!(accion || entidad || actorUserId || q.trim() || fechaDia !== todayISO);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setFechaDia(todayISO);
    setAccion("");
    setEntidad("");
    setActorUserId("");
    setSearchInput("");
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Auditoría</h2>
          <p className="text-xs text-muted-foreground">
            {data
              ? `${data.total} evento${data.total === 1 ? "" : "s"} · ${
                  effectiveFechaDia
                    ? `día ${effectiveFechaDia}`
                    : "todo el histórico"
                }`
              : "Cargando…"}
            {isFetching && !isLoading && " · actualizando"}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-3">
        <div className="flex flex-wrap items-end gap-2">
          {/* Fecha con navegación */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Día
            </label>
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline"
                disabled={!fechaDia}
                onClick={() => { setFechaDia(shiftDay(fechaDia, -1)); setPage(1); }}
                title="Día anterior"
                className="h-8 w-8 p-0"
              >
                <ChevronLeft size={14} />
              </Button>
              <Input
                type="date"
                className="h-8 w-36"
                value={fechaDia ?? ""}
                onChange={(e) => {
                  setFechaDia(e.target.value || null);
                  setPage(1);
                }}
              />
              <Button
                size="sm" variant="outline"
                disabled={!fechaDia || fechaDia >= todayISO}
                onClick={() => { setFechaDia(shiftDay(fechaDia, 1)); setPage(1); }}
                title="Día siguiente"
                className="h-8 w-8 p-0"
              >
                <ChevronRight size={14} />
              </Button>
              <Button
                size="sm"
                variant={fechaDia === null ? "default" : "ghost"}
                onClick={() => { setFechaDia(fechaDia ? null : todayISO); setPage(1); }}
                title={fechaDia ? "Quitar filtro de fecha" : "Volver a hoy"}
              >
                <Calendar size={12} />
                {fechaDia === null ? "Ver todo" : "Todo"}
              </Button>
            </div>
          </div>

          {/* Acción */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Acción
            </label>
            <Select
              value={accion}
              onChange={(e) => { setAccion(e.target.value); setPage(1); }}
              className="w-48"
            >
              <option value="">Todas</option>
              {acciones.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a] ?? a}
                </option>
              ))}
            </Select>
          </div>

          {/* Entidad */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Entidad
            </label>
            <Select
              value={entidad}
              onChange={(e) => { setEntidad(e.target.value); setPage(1); }}
              className="w-36"
            >
              <option value="">Todas</option>
              <option value="cliente">Cliente</option>
              <option value="contrato">Contrato</option>
              <option value="factura">Factura</option>
              <option value="pago">Pago</option>
            </Select>
          </div>

          {/* Actor */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Usuario
            </label>
            <Select
              value={actorUserId}
              onChange={(e) => { setActorUserId(e.target.value); setPage(1); }}
              className="w-52"
            >
              <option value="">Todos</option>
              {actores.map((a) => (
                <option key={a.id} value={a.id}>{a.email}</option>
              ))}
            </Select>
          </div>

          {hasAnyFilter && (
            <Button size="sm" variant="ghost" onClick={resetFilters} className="mb-0">
              <X size={12} /> Limpiar
            </Button>
          )}
        </div>

        {/* Buscador full-text (instantáneo, debounced 300ms) */}
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Buscar — motivo, UUID, código, valores del diff, email
          </label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder='ej. "COBYTE-00123", "por falta de pago", "MAC"…'
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
              className="pl-8"
            />
          </div>
          {q.trim() && fechaDia && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Buscando en todo el histórico (el filtro de día está temporalmente ignorado mientras buscas).
            </p>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30px]"></TableHead>
              <TableHead className="w-[180px]">Fecha y hora</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead>Entidad</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Resumen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={History}
                  title="Sin eventos"
                  description={
                    hasAnyFilter
                      ? "No hay eventos que coincidan con los filtros aplicados."
                      : "No hay eventos registrados en el rango actual."
                  }
                />
              </TableEmpty>
            ) : (
              items.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  expanded={expanded.has(entry.id)}
                  onToggle={() => toggleExpand(entry.id)}
                />
              ))
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button size="sm" variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Fila de audit (clickable, expande para mostrar diff)
// ─────────────────────────────────────────────────────────────────────────
type RowProps = {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
};

function AuditRow({ entry, expanded, onToggle }: RowProps) {
  const changes = extractChanges(entry.payload_antes, entry.payload_despues);
  const motivo = (entry.payload_despues?.__motivo__ as string | undefined) ?? null;
  const actionLabel = ACTION_LABEL[entry.accion] ?? entry.accion;
  const actionVariant = ACTION_VARIANT[entry.accion] ?? "default";
  const entidadLabelShort = entry.entidad
    ? ENTIDAD_LABEL[entry.entidad] ?? entry.entidad
    : "—";

  return (
    <>
      <TableRow onClick={onToggle} className="cursor-pointer">
        <TableCell className="text-muted-foreground">
          {expanded ? <ChevronDown size={14} /> : <ChevronRightIcon size={14} />}
        </TableCell>
        <TableCell className="font-mono text-[11px] whitespace-nowrap">
          {formatDateTime(entry.created_at)}
        </TableCell>
        <TableCell>
          <Badge variant={actionVariant}>{actionLabel}</Badge>
        </TableCell>
        <TableCell>
          {entry.entidad_label ? (
            <div className="flex flex-col gap-0.5 text-xs">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {entidadLabelShort}
              </span>
              {entry.entidad_link ? (
                <Link
                  to={entry.entidad_link}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  {entry.entidad_label}
                  <ExternalLink size={10} className="opacity-50" />
                </Link>
              ) : (
                <span>{entry.entidad_label}</span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">
              {entidadLabelShort}
              {entry.entidad_id && (
                <span className="font-mono text-[10px] ml-1 opacity-70">
                  {entry.entidad_id.slice(0, 8)}…
                </span>
              )}
            </span>
          )}
        </TableCell>
        <TableCell className="text-xs">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <UserIcon size={11} />
            {entry.actor_email ?? "sistema"}
          </span>
        </TableCell>
        <TableCell className="max-w-[380px] text-xs">
          <div className="flex flex-col gap-0.5">
            {changes.length > 0 && (
              <span className="text-muted-foreground">
                {changes.length} campo{changes.length === 1 ? "" : "s"} cambiado
                {changes.length === 1 ? "" : "s"}
              </span>
            )}
            {motivo && (
              <span className="truncate italic">
                <span className="font-medium not-italic">Motivo: </span>
                {motivo}
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Fila expandida: diff completo */}
      {expanded && (
        <TableRow className="bg-muted/20">
          <TableCell colSpan={6} className="p-3">
            {changes.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Activity size={12} />
                Evento sin cambios de campos (acción registrada como marca).
              </div>
            ) : (
              <div className="rounded border bg-card p-2">
                <h4 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                  Cambios
                </h4>
                <ul className="flex flex-col gap-1">
                  {changes.map((c) => (
                    <li
                      key={c.field}
                      className="flex flex-wrap items-center gap-1.5 text-xs"
                    >
                      <span className="font-medium">{labelFor(c.field)}:</span>
                      <span
                        className={cn(
                          "text-muted-foreground",
                          c.before !== undefined && "line-through",
                        )}
                      >
                        {formatValue(c.before)}
                      </span>
                      <ArrowRight size={10} className="text-muted-foreground" />
                      <span className="font-mono text-foreground">
                        {formatValue(c.after)}
                      </span>
                    </li>
                  ))}
                </ul>
                {entry.entidad_id && (
                  <p className="mt-2 text-[10px] text-muted-foreground font-mono">
                    Entidad ID: {entry.entidad_id}
                  </p>
                )}
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
